import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n/config";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEO } from "@/components/SEO";
import { SEOBreadcrumb } from "@/components/SEOBreadcrumb";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { Check, X, Star, Crown, Building2, Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { getStoredAffiliateCode } from "@/hooks/useAffiliateTracking";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DowngradeModal } from "@/components/DowngradeModal";
import { 
  formatDiscountMessage, 
  saveCouponData, 
  clearCouponData,
  getFormattedProductList,
  validateCouponGlobally,
  hasValidCoupons,
  PRODUCT_NAMES,
  type CouponResult 
} from "@/lib/couponUtils";

interface PackageInfo {
  id: "base" | "pro" | "plus";
  price: number;
  icon: typeof Star;
  bgColor: string;
  borderColor: string;
}

const packages: PackageInfo[] = [
  { id: "base", price: 70, icon: Star, bgColor: "bg-slate-400", borderColor: "border-slate-600" },
  { id: "pro", price: 95, icon: Crown, bgColor: "bg-amber-400", borderColor: "border-amber-600" },
  { id: "plus", price: 185, icon: Building2, bgColor: "bg-violet-400", borderColor: "border-violet-600" },
];

const SubscriptionPlans = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, hasCompletedScreening, screeningStatus, subscriptionType, subscriptionEndDate } = useAuth();
  const [selectedPackage, setSelectedPackage] = useState<PackageInfo | null>(null);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showDowngradeDialog, setShowDowngradeDialog] = useState(false);
  const [pendingPlanChange, setPendingPlanChange] = useState<PackageInfo | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Coupon state
  const [couponCode, setCouponCode] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupons, setAppliedCoupons] = useState<Record<string, CouponResult | null>>({});

  // Read coupon from URL or sessionStorage on mount
  useEffect(() => {
    const urlCoupon = searchParams.get('coupon');
    const savedCoupon = sessionStorage.getItem('pendingCoupon');
    const savedCouponData = sessionStorage.getItem('appliedCouponData');
    
    if (urlCoupon) {
      // Coupon from URL = first visit, show toast
      setCouponCode(urlCoupon.toUpperCase());
      validateCouponForPlans(urlCoupon.toUpperCase(), true);
    } else if (savedCoupon) {
      setCouponCode(savedCoupon);
      
      // Try to load saved data silently
      if (savedCouponData) {
        try {
          const parsedData = JSON.parse(savedCouponData);
          // Extract only relevant plans for this page
          const relevantData: Record<string, CouponResult | null> = {};
          ['base', 'pro', 'plus'].forEach(plan => {
            if (parsedData[plan]) {
              relevantData[plan] = parsedData[plan];
            }
          });
          setAppliedCoupons(relevantData);
          // No toast - just restore data
        } catch (e) {
          validateCouponForPlans(savedCoupon, false);
        }
      } else {
        validateCouponForPlans(savedCoupon, false);
      }
    }
  }, []);

  const validateCouponForPlans = async (code: string, showToast = true) => {
    setCouponLoading(true);
    
    try {
      // Validate against ALL product types globally
      const allResults = await validateCouponGlobally(supabase, code);
      
      // Check if any product is valid globally
      const hasAnyValid = hasValidCoupons(allResults);
      
      if (hasAnyValid) {
        // Save ALL results to sessionStorage
        saveCouponData(code, allResults);
        setAppliedCoupons(allResults);
        
        if (showToast) {
          const locale = i18n.language || 'it';
          const formattedProducts = getFormattedProductList(allResults, locale);
          toast.success(t("pricing.coupon.validDynamic", { 
            products: formattedProducts,
            discount: formatDiscountMessage(
              allResults[Object.keys(allResults).find(k => allResults[k]?.valid) || '']?.discountType || 'percentage',
              allResults[Object.keys(allResults).find(k => allResults[k]?.valid) || '']?.discountValue || 0
            )
          }));
        }
      } else {
        setAppliedCoupons({});
        if (showToast) {
          toast.error(t("pricing.coupon.invalid"));
        }
      }
    } catch (error) {
      console.error("Error validating coupon:", error);
      if (showToast) {
        toast.error(t("pricing.coupon.invalid"));
      }
    } finally {
      setCouponLoading(false);
    }
  };

  const handleApplyCoupon = () => {
    if (!couponCode.trim()) return;
    validateCouponForPlans(couponCode.trim().toUpperCase(), true);
  };

  const handleRemoveCoupon = () => {
    setCouponCode("");
    setAppliedCoupons({});
    sessionStorage.removeItem('pendingCoupon');
    sessionStorage.removeItem('appliedCouponData');
  };

  const calculateFinalPrice = (pkg: PackageInfo): number => {
    const coupon = appliedCoupons[pkg.id];
    if (!coupon) return pkg.price;
    
    if (coupon.discountType === 'fixed') {
      return Math.max(0, pkg.price - coupon.discountValue);
    } else if (coupon.discountType === 'full') {
      return 0;
    } else {
      return pkg.price * (1 - coupon.discountPercent / 100);
    }
  };

  const getDiscountMessage = (pkg: PackageInfo): string | null => {
    const coupon = appliedCoupons[pkg.id];
    if (!coupon) return null;
    
    if (coupon.discountType === 'full') {
      return t('pricing.coupon.valid100');
    } else if (coupon.discountType === 'fixed') {
      return t('pricing.coupon.validFixedDiscount', { amount: coupon.discountValue });
    } else {
      return t('pricing.coupon.validDiscount', { percent: coupon.discountPercent });
    }
  };

  const hasAnyCouponApplied = Object.values(appliedCoupons).some(Boolean);

  const featureRows = [
    { key: "verifiedProfile", base: true, pro: true, plus: true },
    { key: "searchVisibility", base: true, pro: true, plus: true },
    { key: "directContacts", base: true, pro: true, plus: true },
    { key: "verifiedReviews", base: true, pro: true, plus: true },
    { key: "partnerDiscounts", base: true, pro: true, plus: true },
    { key: "proBadge", base: false, pro: true, plus: true },
    { key: "priorityRanking", base: false, pro: true, plus: true },
    { key: "multilingualBio", base: false, pro: true, plus: true },
    { key: "coverMedia", base: false, pro: true, plus: true },
    { key: "eventCreation", base: false, pro: true, plus: true },
    { key: "plusBadge", base: false, pro: false, plus: true },
    { key: "companyPage", base: false, pro: false, plus: true },
    { key: "teamMembers", base: false, pro: false, plus: true },
    { key: "companyMultilingual", base: false, pro: false, plus: true },
    { key: "jobPostingCredits", base: false, pro: false, plus: true },
  ];

  const handleCheckout = async (pkg: PackageInfo) => {
    if (!isAuthenticated) {
      toast.error(t("pricing.errors.loginRequired"));
      navigate("/auth/login");
      return;
    }

    if (!hasCompletedScreening || screeningStatus !== "approved") {
      toast.error(t("pricing.error.screeningRequired"));
      navigate("/become-professional");
      return;
    }

    // Check if this is the current plan
    if (subscriptionType === pkg.id) {
      toast.info(t("subscriptionPlans.alreadySubscribed"));
      setSelectedPackage(null);
      return;
    }

    setIsCheckingOut(true);
    
    const affiliateCode = getStoredAffiliateCode();
    
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: {
          planType: pkg.id,
          billingType: "annual",
          affiliateCode: affiliateCode || undefined,
        },
      });

      if (error) throw error;

      if (data.success) {
        toast.success(data.message);
        setSelectedPackage(null);
        navigate("/dashboard");
      } else if (data.url) {
        window.open(data.url, "_blank");
        setSelectedPackage(null);
      }
    } catch (error: any) {
      toast.error(error.message || t("subscriptionPlans.checkoutError"));
    } finally {
      setIsCheckingOut(false);
    }
  };

  const renderCellValue = (value: boolean) => {
    return value ? (
      <Check className="w-5 h-5 text-primary mx-auto" />
    ) : (
      <X className="w-5 h-5 text-muted-foreground/50 mx-auto" />
    );
  };

  const planHierarchy: Record<string, number> = {
    base: 0,
    pro: 1,
    plus: 2,
  };

  const handlePackageClick = (pkg: PackageInfo) => {
    const btnState = getButtonState(pkg.id);
    
    if (btnState.isDowngrade) {
      setPendingPlanChange(pkg);
      setShowDowngradeDialog(true);
    } else if (subscriptionType && planHierarchy[subscriptionType] !== undefined && 
               planHierarchy[pkg.id] > planHierarchy[subscriptionType]) {
      setPendingPlanChange(pkg);
      setShowUpgradeDialog(true);
    } else {
      setSelectedPackage(pkg);
    }
  };

  const handleUpgradeConfirm = async () => {
    if (!pendingPlanChange) return;
    setShowUpgradeDialog(false);
    setIsProcessing(true);

    try {
      const { data, error } = await supabase.functions.invoke("update-subscription", {
        body: { newPlanType: pendingPlanChange.id },
      });
      if (error) throw error;
      if (data.success) {
        toast.success(data.message);
        window.location.reload();
      }
    } catch (error: any) {
      toast.error(error.message || t('pricing.errors.upgradeError'));
    } finally {
      setIsProcessing(false);
      setPendingPlanChange(null);
    }
  };

  const handleDowngradeConfirm = async () => {
    if (!pendingPlanChange) return;
    setShowDowngradeDialog(false);
    setIsProcessing(true);

    try {
      const { data, error } = await supabase.functions.invoke("update-subscription", {
        body: { newPlanType: pendingPlanChange.id },
      });
      if (error) throw error;
      if (data.success) {
        toast.success(data.message);
        window.location.reload();
      }
    } catch (error: any) {
      toast.error(error.message || t('pricing.errors.downgradeError'));
    } finally {
      setIsProcessing(false);
      setPendingPlanChange(null);
    }
  };

  const getButtonState = (pkgId: string) => {
    // Current plan
    if (subscriptionType === pkgId) {
      return { 
        text: t("pricing.plans.currentPlan"), 
        disabled: true, 
        variant: "secondary" as const,
        isDowngrade: false 
      };
    }
    
    // If user has an active plan, determine upgrade or downgrade
    if (subscriptionType && planHierarchy[subscriptionType] !== undefined) {
      const currentLevel = planHierarchy[subscriptionType];
      const targetLevel = planHierarchy[pkgId];
      
      if (targetLevel < currentLevel) {
        // DOWNGRADE - text link
        return { 
          text: t("pricing.plans.downgrade"), 
          disabled: false, 
          variant: "outline" as const,
          isDowngrade: true 
        };
      } else {
        // UPGRADE - prominent button
        return { 
          text: t("pricing.plans.upgrade"), 
          disabled: false, 
          variant: "primary" as const,
          isDowngrade: false 
        };
      }
    }
    
    // No active plan - show "Buy"
    return { 
      text: t("subscriptionPlans.buyPlan", { plan: t(`subscriptionPlans.packages.${pkgId}.name`) }), 
      disabled: false, 
      variant: pkgId === "pro" ? "primary" as const : "outline" as const,
      isDowngrade: false 
    };
  };

  return (
    <div className="min-h-screen flex flex-col bg-surface">
      <SEO
        title={t("subscriptionPlans.seoTitle")}
        description={t("subscriptionPlans.seoDescription")}
      />
      <Header />

      <main className="flex-1 container mx-auto px-4 py-12">
        <div className="max-w-5xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <SEOBreadcrumb items={[
              { label: t('pricing.breadcrumb'), href: '/pricing' },
              { label: t('subscriptionPlans.breadcrumb') }
            ]} />
            <h1 className="font-heading font-bold text-4xl md:text-5xl text-dark mb-4">
              {t("subscriptionPlans.heroTitle")}
            </h1>
            <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
              {t("subscriptionPlans.heroSubtitle")}
            </p>
          </div>

          {/* Coupon Input Section */}
          <BrutalCard className="max-w-xl mx-auto mb-12">
            <div className="flex flex-col sm:flex-row gap-4">
              <BrutalInput
                placeholder={t("pricing.coupon.placeholder")}
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                className="flex-1"
                disabled={hasAnyCouponApplied}
              />
              {!hasAnyCouponApplied ? (
                <BrutalButton
                  onClick={handleApplyCoupon}
                  disabled={!couponCode.trim() || couponLoading}
                  className="whitespace-nowrap"
                >
                  {couponLoading ? t('pricing.coupon.validating') : t('pricing.coupon.apply')}
                </BrutalButton>
              ) : (
                <BrutalButton
                  onClick={handleRemoveCoupon}
                  variant="outline"
                  className="whitespace-nowrap flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  {t("pricing.coupon.remove")}
                </BrutalButton>
              )}
            </div>
            {hasAnyCouponApplied && (() => {
              const sample = Object.values(appliedCoupons).find(Boolean);
              if (!sample) return null;
              const discount = formatDiscountMessage(sample.discountType, sample.discountValue);
              const locale = i18n.language || 'it';
              const formattedProducts = getFormattedProductList(appliedCoupons, locale);
              return (
                <div className="mt-4 p-4 bg-primary/10 border-[3px] border-primary">
                  <p className="font-heading font-bold text-dark">
                    {t('pricing.coupon.couponConfirmation', { discount })}
                  </p>
                  <p className="font-body text-sm text-muted-foreground mt-1">
                    {t('pricing.coupon.appliesToFormatted', { products: formattedProducts })}
                  </p>
                </div>
              );
            })()}
          </BrutalCard>

          {/* Package Cards Header */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {packages.map((pkg) => {
              const Icon = pkg.icon;
              const isCurrent = subscriptionType === pkg.id;
              const coupon = appliedCoupons[pkg.id];
              const finalPrice = calculateFinalPrice(pkg);
              const discountMessage = getDiscountMessage(pkg);
              
              return (
                <BrutalCard 
                  key={pkg.id} 
                  className={`text-center relative ${isCurrent ? "border-success border-[4px] bg-success/5" : ""} ${pkg.id === "pro" && !isCurrent ? "border-primary border-[4px]" : ""}`}
                >
                  {isCurrent && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-success px-4 py-1 border-[3px] border-dark">
                      <span className="font-heading font-bold text-xs uppercase text-white">
                        {t('pricing.plans.yourPlan')}
                      </span>
                    </div>
                  )}
                  {pkg.id === "pro" && !isCurrent && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary px-4 py-1 border-[3px] border-dark">
                      <span className="font-heading font-bold text-xs uppercase">
                        {t('pricing.plans.popular')}
                      </span>
                    </div>
                  )}
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-none border-[3px] ${pkg.bgColor} ${pkg.borderColor} flex items-center justify-center`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="font-heading font-bold text-2xl text-dark mb-2">
                    {t(`subscriptionPlans.packages.${pkg.id}.name`)}
                  </h2>
                  <p className="text-muted-foreground text-sm mb-4">
                    {t(`subscriptionPlans.packages.${pkg.id}.tagline`)}
                  </p>
                  <div className="font-heading font-bold text-3xl text-dark">
                    {coupon ? (
                      <>
                        <span className="text-lg line-through text-muted-foreground mr-2">
                          €{pkg.price}
                        </span>
                        <span className="text-primary">€{finalPrice.toFixed(0)}</span>
                      </>
                    ) : (
                      <>€{pkg.price}</>
                    )}
                    <span className="text-base font-normal text-muted-foreground ml-1">
                      {t("subscriptionPlans.table.perYear")}
                    </span>
                  </div>
                  {discountMessage && (
                    <p className="text-sm text-primary mt-2 font-medium">
                      {discountMessage}
                    </p>
                  )}
                  {hasAnyCouponApplied && !coupon && (
                    <p className="text-sm text-muted-foreground mt-2">
                      {t("visibilityPackages.coupon.notApplicable")}
                    </p>
                  )}
                </BrutalCard>
              );
            })}
          </div>

          {/* Comparison Table */}
          <BrutalCard className="overflow-hidden mb-8">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-[3px] border-dark">
                    <th className="text-left p-4 font-heading font-bold text-dark">
                      {t("subscriptionPlans.table.feature")}
                    </th>
                    <th className="text-center p-4 font-heading font-bold text-dark min-w-[120px]">
                      <div className="flex items-center justify-center gap-2">
                        <Star className="w-5 h-5 text-slate-500" />
                        {t("subscriptionPlans.packages.base.name")}
                      </div>
                    </th>
                    <th className="text-center p-4 font-heading font-bold text-dark min-w-[120px]">
                      <div className="flex items-center justify-center gap-2">
                        <Crown className="w-5 h-5 text-amber-500" />
                        {t("subscriptionPlans.packages.pro.name")}
                      </div>
                    </th>
                    <th className="text-center p-4 font-heading font-bold text-dark min-w-[120px]">
                      <div className="flex items-center justify-center gap-2">
                        <Building2 className="w-5 h-5 text-violet-500" />
                        {t("subscriptionPlans.packages.plus.name")}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {featureRows.map((row, index) => (
                    <tr
                      key={row.key}
                      className={index < featureRows.length - 1 ? "border-b border-border" : ""}
                    >
                      <td className="p-4">
                        <div className="font-body font-medium text-dark">
                          {t(`subscriptionPlans.table.features.${row.key}.label`)}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {t(`subscriptionPlans.table.features.${row.key}.description`)}
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        {renderCellValue(row.base)}
                      </td>
                      <td className="p-4 text-center">
                        {renderCellValue(row.pro)}
                      </td>
                      <td className="p-4 text-center">
                        {renderCellValue(row.plus)}
                      </td>
                    </tr>
                  ))}
                  {/* Price Row */}
                  <tr className="border-t-[3px] border-dark bg-muted/30">
                    <td className="p-4 font-heading font-bold text-dark">
                      {t("subscriptionPlans.table.price")}
                    </td>
                    {packages.map((pkg) => {
                      const coupon = appliedCoupons[pkg.id];
                      const finalPrice = calculateFinalPrice(pkg);
                      return (
                        <td key={pkg.id} className="p-4 text-center font-heading font-bold text-2xl text-dark">
                          {coupon ? (
                            <>
                              <span className="text-base line-through text-muted-foreground mr-1">€{pkg.price}</span>
                              <span className="text-primary">€{finalPrice.toFixed(0)}</span>
                            </>
                          ) : (
                            `€${pkg.price}`
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  {/* CTA Row */}
                  <tr>
                    <td className="p-4"></td>
                    {packages.map((pkg) => {
                      const btnState = getButtonState(pkg.id);
                      return (
                        <td key={pkg.id} className="p-4 text-center">
                          {btnState.isDowngrade ? (
                            <button
                              onClick={() => handlePackageClick(pkg)}
                              className="w-full text-center text-muted-foreground hover:text-foreground 
                                         underline font-body text-sm py-2 transition-colors"
                            >
                              {btnState.text}
                            </button>
                          ) : (
                            <BrutalButton
                              onClick={() => handlePackageClick(pkg)}
                              variant={btnState.variant}
                              disabled={btnState.disabled || isProcessing}
                              className="w-full max-w-[140px]"
                            >
                              {btnState.text}
                            </BrutalButton>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </BrutalCard>

          {/* Back to Pricing Link */}
          <div className="text-center">
            <BrutalButton variant="outline" onClick={() => navigate("/pricing")}>
              {t("subscriptionPlans.backToPricing")}
            </BrutalButton>
          </div>
        </div>
      </main>

      <Footer />

      {/* Confirmation Modal */}
      <Dialog open={!!selectedPackage} onOpenChange={() => setSelectedPackage(null)}>
        <DialogContent className="border-[3px] border-dark shadow-[6px_6px_0px_#101322]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {selectedPackage && t(`subscriptionPlans.packages.${selectedPackage.id}.name`)}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {selectedPackage && t("subscriptionPlans.modal.description", {
                name: t(`subscriptionPlans.packages.${selectedPackage.id}.name`),
                price: selectedPackage.price,
              })}
            </DialogDescription>
          </DialogHeader>

          {selectedPackage && (
            <div className="py-4">
              <ul className="space-y-2">
                {(t(`subscriptionPlans.packages.${selectedPackage.id}.benefits`, { returnObjects: true }) as string[]).map(
                  (benefit, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      {benefit}
                    </li>
                  )
                )}
              </ul>
            </div>
          )}

          <DialogFooter className="gap-2">
            <BrutalButton variant="outline" onClick={() => setSelectedPackage(null)} disabled={isCheckingOut}>
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton
              onClick={() => selectedPackage && handleCheckout(selectedPackage)}
              disabled={isCheckingOut}
              className="gap-2"
            >
              {isCheckingOut ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t("subscriptionPlans.processing")}
                </>
              ) : (
                t("subscriptionPlans.confirmBuy", { price: selectedPackage?.price })
              )}
            </BrutalButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upgrade Confirmation Dialog */}
      <AlertDialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <AlertDialogContent className="border-[3px] border-dark shadow-[6px_6px_0px_#101322]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">{t('pricing.upgrade.confirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('pricing.upgrade.confirmDesc', {
                plan: pendingPlanChange ? t(`subscriptionPlans.packages.${pendingPlanChange.id}.name`) : '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingPlanChange(null)}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleUpgradeConfirm} disabled={isProcessing}>
              {isProcessing ? t('pricing.plans.loading') : t('pricing.upgrade.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Downgrade Confirmation Dialog */}
      <DowngradeModal
        open={showDowngradeDialog}
        onOpenChange={setShowDowngradeDialog}
        currentPlan={subscriptionType || ''}
        targetPlan={pendingPlanChange?.id || ''}
        renewalDate={subscriptionEndDate}
        isProcessing={isProcessing}
        onConfirm={handleDowngradeConfirm}
        onCancel={() => {
          setShowDowngradeDialog(false);
          setPendingPlanChange(null);
        }}
      />
    </div>
  );
};

export default SubscriptionPlans;
